/**
 * Two of the dashboard's three summary panels, from the prototype's `dash__grid`.
 *
 * The prototype's row is Active Learning Paths | Upcoming deadlines | Recent certificates
 * (index.html:5125-5162). The first of those is `DashboardPaths.tsx`; these are the other two.
 * The paths panel used to be absent here — Open edX programs came from the course-discovery
 * service, which this install does not run — and that is no longer true: OpenCraft's
 * `learning-paths-plugin` serves them from the LMS itself and `shared/lib/paths.ts` reads it.
 *
 * NO PATHWAY ROW BELONGS IN THE DEADLINES PANEL, and that is a checked statement rather than
 * a layout preference. `LearningPath` has no deadline, end-date or expiry field at all; the
 * only date in the plugin is `LearningPathStep.course_dates`, which returns the step's own
 * COURSE's (start, end). Every date a pathway could contribute is therefore already a course
 * date, and every course in a pathway a learner is taking is already an enrolment this panel
 * fans out over — so surfacing them again under the path's name would print the same deadline
 * twice. The pathway's own panel states progress instead, which is a thing it really knows.
 *
 * Every panel renders nothing at all when it has nothing to say. A dashboard that always
 * shows "Upcoming deadlines (0)" teaches people to stop reading it.
 */

import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Icon, Spinner } from '../../shared/ui';
import { loadSession } from '../../shared/lib/session';
import {
  certificateViewUrl,
  formatIssued,
  listEarnedCertificates,
  type EarnedCertificate,
} from '../../shared/lib/certificates';
import {
  listMyPathwayCertificates,
  pathwayCertificateHref,
} from '../../shared/lib/pathway-certificates';
import { upcomingDeadlines, type Deadline } from '../../shared/lib/deadlines';

/* ------------------------------------------------------------------ deadlines ---- */

/**
 * ONE REQUEST, and it no longer takes a course list.
 *
 * This used to be a `useQueries` fan-out — one `/api/course_home/dates/{id}` per enrolled
 * course — which is why it needed `courseIds` and why the dashboard had to wait for its
 * enrolments before this panel could start. The scope now comes from the session and the
 * Host on the server side, so the panel asks its own question directly and in parallel with
 * everything else on the page.
 *
 * The failure rule is unchanged in effect: the panel renders nothing at all when it has
 * nothing to say, and a failed request is one of those cases. It differs in one honest
 * respect — a single course whose block structure cannot be read used to lose that one
 * course and keep the rest, and the server reproduces that deliberately (it logs and skips
 * the course), but a failure of the whole request now loses the whole panel rather than
 * part of it. That is the trade a single call makes, and it is stated rather than hidden.
 */
export function DeadlinesPanel() {
  const deadlinesQuery = useQuery({
    queryKey: ['learner-dates'],
    queryFn: () => upcomingDeadlines(),
    staleTime: 5 * 60_000,
    retry: false,
  });

  const loading = deadlinesQuery.isLoading;
  const deadlines: Deadline[] = (deadlinesQuery.data ?? []).slice(0, 4);

  if (deadlinesQuery.isError) return null;
  if (!loading && deadlines.length === 0) return null;

  return (
    <div className="card dash__panel">
      <div className="section-head">
        <h3>Upcoming deadlines</h3>
      </div>
      {loading ? (
        <Spinner label="Checking your dates…" />
      ) : (
        <ul className="dash__panel-list">
          {deadlines.map((item) => (
            <li className="deadline" key={`${item.courseId}-${item.title}-${item.date}`}>
              <span className="deadline__dot" data-soon={item.soon || undefined} aria-hidden="true" />
              <div className="deadline__body">
                <span className="deadline__title">{item.title}</span>
                <span className="deadline__meta">
                  {item.due}
                  {item.courseName ? ` · ${item.courseName}` : ''}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* --------------------------------------------------------------- certificates ---- */

/**
 * BOTH KINDS OF CREDENTIAL, newest first.
 *
 * A learner finishing a pathway usually earns its last course certificate seconds earlier,
 * so "recent" only means anything if the two sources are merged and sorted rather than
 * concatenated. The pathway rows carry a chip and a different icon; without it two rows
 * that look identical would claim very different things.
 */
interface PanelRow {
  id: string;
  kind: 'course' | 'pathway';
  title: string;
  issued: string;
  href: string | null;
}

export function CertificatesPanel() {
  const session = useQuery({ queryKey: ['session'], queryFn: loadSession, staleTime: 5 * 60_000 });
  const username = session.data?.user?.username;

  const certificates = useQuery({
    queryKey: ['my-certificates', username],
    queryFn: () => listEarnedCertificates(username!),
    enabled: Boolean(username),
    // Advisory panel: a failure here must not disturb the rest of the dashboard.
    retry: false,
  });

  // Its own query, and its own failure. An academy with no pathways, or a pathways API that
  // is briefly unavailable, must still show the course certificates this panel always had.
  const pathways = useQuery({
    queryKey: ['my-pathway-certificates'],
    queryFn: listMyPathwayCertificates,
    enabled: Boolean(username),
    retry: false,
  });

  const rows: PanelRow[] = [
    ...(certificates.data ?? [])
      .filter((c) => c.is_passing)
      .map((c) => ({
        id: `course:${c.uuid || c.course_id}`,
        kind: 'course' as const,
        title: c.course_display_name,
        issued: c.created_date,
        href: certificateViewUrl(c),
      })),
    ...(pathways.data ?? []).map((c) => ({
      id: `pathway:${c.uuid}`,
      kind: 'pathway' as const,
      title: c.pathway.title,
      issued: c.issued ?? '',
      href: pathwayCertificateHref(c.uuid),
    })),
  ].sort((a, b) => (Date.parse(b.issued) || 0) - (Date.parse(a.issued) || 0));

  const earned = rows.slice(0, 3);
  // Only a failure of BOTH sources hides the panel. One source failing simply shows the
  // other's rows, which is more useful than an empty space and more honest than a spinner
  // that never resolves.
  if ((certificates.isError && pathways.isError) || earned.length === 0) return null;

  return (
    <div className="card dash__panel">
      <div className="section-head">
        <h3>Recent certificates</h3>
        <Link to="/certificates" className="dash__panel-all">
          All <Icon name="arrow_forward" />
        </Link>
      </div>
      <ul className="dash__panel-list">
        {earned.map((row) => (
          <li className="dash__cert" key={row.id} data-kind={row.kind}>
            <span className="dash__cert-icon" aria-hidden="true">
              <Icon name={row.kind === 'pathway' ? 'route' : 'workspace_premium'} />
            </span>
            <div className="dash__cert-body">
              <span className="dash__cert-name">
                {row.title}
                {row.kind === 'pathway' && (
                  <span className="dash__cert-chip">Learning path</span>
                )}
              </span>
              <span className="dash__cert-date">{formatIssued(row.issued)}</span>
            </div>
            {/* `<Link>` now, not `<a>`. Both certificate documents are routes in THIS app:
                the course one used to be the platform's server-rendered page reached through
                the proxy, and nothing proxies that path any more. A `null` href means the
                platform published neither a download nor a web view — no link is better than
                a dead one. */}
            {row.href && (
              <Link className="ui-btn" data-variant="ghost" to={row.href}>
                View
              </Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export type { EarnedCertificate };
